import React, { lazy, Suspense, forwardRef } from "react";

// Lazy boundary around @mux/mux-player-react.
//
// WHY: importing MuxPlayer directly pulls the entire video stack —
// hls.js (~1.5MB), media-chrome (~460KB) and @mux/* (~400KB) — into
// whatever chunk references it. Several video components (ContentCard,
// FilmWatch, AFSMode, AfsCompare, CertificationPage) imported it
// statically, so ~3MB of video libraries landed in the main entry
// chunk and were parsed on every cold boot — even for coaches who never
// opened a video. On iOS 26's tightened WKWebView watchdog that boot-time
// parse was a real crash risk; on web it's just slow cold starts.
//
// Wrapping the import in React.lazy moves the whole video stack into its
// own async chunk that only downloads + parses the first time a player
// actually renders. The chunk is cached after that, so subsequent videos
// are instant.
//
// Ref forwarding: FilmWatch / AFSMode / AfsCompare control playback
// (currentTime, play/pause, seek) via a ref to the underlying media
// element. React.lazy forwards refs to a ref-forwarding default export,
// and @mux/mux-player-react's default export is one — the explicit
// forwardRef here makes that contract obvious and guarantees the ref
// reaches the resolved <mux-player> element.
const MuxPlayerLazy = lazy(() => import("@mux/mux-player-react"));

// Minimal dark placeholder shown only on the first-ever video open
// (before the chunk resolves). Fills whatever box the parent gives it,
// and respects an explicit `style` when the call site sizes the player
// directly. Matches the app's #000 video surface so there's no flash.
function VideoFallback({ style }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#000",
        ...style,
      }}
    />
  );
}

// Shown when the player fails to load a video — a bad/stale/purged Mux
// playback id, an asset still processing, or a decode failure. Without this
// every player surface rendered a silent black box with no explanation.
function VideoError({ style, onRetry }) {
  return (
    <div
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#0E0E0E",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        textAlign: "center",
        padding: 16,
        ...style,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: "#bbb" }}>This video couldn't load</div>
      <div style={{ fontSize: 11.5, color: "#777", lineHeight: 1.5, maxWidth: 240 }}>
        It may still be processing or temporarily unavailable.
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{ marginTop: 4, padding: "6px 14px", borderRadius: 7, background: "transparent", border: "1px solid #333", color: "#ccc", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

const LazyMuxPlayer = forwardRef(function LazyMuxPlayer(props, ref) {
  const { onError, style, playbackId } = props;
  const [errored, setErrored] = React.useState(false);
  // Reset on a new video so a previously-failed surface can recover.
  React.useEffect(() => { setErrored(false); }, [playbackId]);

  if (errored) {
    return <VideoError style={style} onRetry={() => setErrored(false)} />;
  }
  return (
    <Suspense fallback={<VideoFallback style={style} />}>
      <MuxPlayerLazy
        {...props}
        ref={ref}
        onError={(e) => {
          setErrored(true);
          try { onError && onError(e); } catch {}
        }}
      />
    </Suspense>
  );
});

export default LazyMuxPlayer;
